import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from '@capacitor/geolocation';
import FlashDurations from "./FlashDurations";
import "swiper/css";
import "../styles/flashes.css";
import { countries } from "countries-list";
import {
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  Popover,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import FilterListIcon from "@mui/icons-material/FilterList";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { formatDistanceToNow } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import BottomNav from './BottomNav';
import Comments from "./FlashComments";
import { CircularProgress } from '@mui/material';

const UserFlashes = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [places, setPlaces] = useState([]);
  const [countriesISO, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(null);
  const [activeVerticalIndex, setActiveVerticalIndex] = useState(0);
  const [activeHorizontalIndex, setActiveHorizontalIndex] = useState(0);
  const [distanceCache, setDistanceCache] = useState({});
  const activeVideoRef = useRef(null);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem('flashes_sound_on') === '1';
    } catch (_) {
      return false;
    }
  });
  const soundOnRef = useRef(false);
  const verticalSwiperRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [taggedUsers, setTaggedUsers] = useState([]);

  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  const stopActiveVideo = () => {
    if (!activeVideoRef.current) return;
    try {
      activeVideoRef.current.pause();
      activeVideoRef.current.currentTime = 0;
    } catch (_) {
      // ignore
    } finally {
      activeVideoRef.current = null;
    }
  };

  const tryPlayVideo = (videoElement) => {
    if (!videoElement) return;
    activeVideoRef.current = videoElement;

    const wantSound = Boolean(soundOnRef.current);
    videoElement.muted = !wantSound;
    videoElement.volume = wantSound ? 1 : 0;

    const playPromise = videoElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        if (wantSound) {
          try {
            videoElement.muted = true;
            videoElement.volume = 0;
            videoElement.play().catch(() => {});
          } catch (_) {}
        }
        console.error("Error autoplaying video:", err);
      });
    }
  };

  const tryPlayActiveVideoInVertical = (verticalSwiper) => {
    if (!verticalSwiper?.slides?.length) return;
    const activeVerticalSlide = verticalSwiper.slides[verticalSwiper.activeIndex];
    if (!activeVerticalSlide) return;

    const horizontalEl = activeVerticalSlide.querySelector('.swiper-horizontal');
    const horizontalSwiper = horizontalEl?.swiper;
    const horizontalActiveSlide = horizontalSwiper?.slides?.[horizontalSwiper.activeIndex];
    const videoElement = horizontalActiveSlide?.querySelector('video');
    tryPlayVideo(videoElement);
  };

  const tryPlayActiveVideoInHorizontal = (horizontalSwiper) => {
    if (!horizontalSwiper?.slides?.length) return;
    const parentVerticalSlide = horizontalSwiper.el?.closest('.swiper-slide');
    if (!parentVerticalSlide || !parentVerticalSlide.classList.contains('swiper-slide-active')) return;

    const activeSlide = horizontalSwiper.slides[horizontalSwiper.activeIndex];
    const videoElement = activeSlide?.querySelector('video');
    tryPlayVideo(videoElement);
  };

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('flashes_sound_on', next ? '1' : '0');
      } catch (_) {}

      if (activeVideoRef.current) {
        try {
          activeVideoRef.current.muted = !next;
          activeVideoRef.current.volume = next ? 1 : 0;
          activeVideoRef.current.play().catch(() => {});
        } catch (_) {}
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        if (Capacitor.getPlatform() === "web") {
          if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                console.log("User's location:", position);
                setUserLocation({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                });
                setLocationPermissionGranted(true);
              },
              (error) => {
                console.error("Error fetching user location:", error);
                setLocationPermissionGranted(false);
              }
            );
          }
          else {
            console.error("Geolocation is not supported by this browser.");
            setLocationPermissionGranted(false);
          }
        }
        else {
          const permission = await Geolocation.requestPermissions();
          if (permission.location === "granted") {
            const position = await Geolocation.getCurrentPosition();
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setLocationPermissionGranted(true);
          }
          else {
            setLocationPermissionGranted(false);
          }
        }
      } catch (err) {
        console.error("Error fetching user location:", err);
      }
    };

    fetchUserLocation();
  }, []);

  const getCountryName = (isoCode) => {
    return countries[isoCode]?.name || "Unknown Country";
  };

  const fetchUserFlashes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { page, limit: 5, specificUserId: userId },
      });

      if (response.data.places.length > 0) {
        setPlaces((prevPlaces) => {
          const newPlaces = response.data.places.filter(
            (newPlace) =>
              !prevPlaces.some(
                (existingPlace) =>
                  existingPlace.place_name === newPlace.place_name &&
                  existingPlace.country === newPlace.country
              )
          );
          return [...prevPlaces, ...newPlaces];
        });
        setPage((prevPage) => prevPage + 1);
      }
      else {
        console.log("No more flashes to load.");
      }
    } catch (err) {
      console.error("Error fetching user flashes:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, page]);

  useEffect(() => {
    fetchUserFlashes();
    fetchCountries();
  }, [fetchUserFlashes]);

  const handleLike = async (flash_id, is_liked) => {
    console.log(flash_id);
    try {
      if (is_liked) {
        await axiosInstance.delete(`/flash-likes`, { data: { flash_id } });
      }
      else {
        await axiosInstance.post(`/flash-likes`, { flash_id, flash_owner: userId });
      }

      setPlaces((prevPlaces) =>
        prevPlaces.map((place) => ({
          ...place,
          posts: place.posts.map((post) =>
            post.flash_id === flash_id
              ? { ...post, is_liked: !is_liked }
              : post
          ),
        }))
      );
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const openDrawer = (flashId) => {
    setSelectedPostId(flashId);
    setIsDrawerOpen(true);
    document.body.style.overflow = "hidden";
    document.querySelector(".bottom-nav")?.classList.add("hidden");
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedPostId(null);
    document.body.style.overflow = "";
    document.querySelector(".bottom-nav")?.classList.remove("hidden");
  };

  const applyFilter = async () => {
    try {
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { country: selectedCountry, city: selectedCity },
      });
      setPlaces(response.data.places);
      setIsFilterDialogOpen(false);
    } catch (err) {
      console.error("Error applying filter:", err);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/flashes/countries");
      setCountries(response.data.countries);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  const fetchCities = async (country) => {
    try {
      const response = await axiosInstance.get("/flashes/cities", {
        params: { country },
      });
      setCities(response.data.cities);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const redirectToGoogleMaps = (latitude, longitude) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(googleMapsUrl, "_blank");
  };

  const getTimeAgo = (utcTimestamp) => {
    const utcDate = fromZonedTime(new Date(utcTimestamp), "UTC");
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = toZonedTime(utcDate, userTimeZone);

    return formatDistanceToNow(localTime, { addSuffix: true }).replace(/^about /, '');
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setSelectedCity("");
    fetchCities(country);
  };

  const resetFilters = async () => {
    setSelectedCountry("");
    setSelectedCity("");
    try {
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { page: 1, limit: 5 },
      });
      setPlaces(response.data.places);
      setPage(1);
      setIsFilterDialogOpen(false);
    } catch (err) {
      console.error("Error resetting filters:", err);
    }
  };

  const handleOpenPopover = (event, tags) => {
    setAnchorEl(event.currentTarget);
    setTaggedUsers(tags);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setTaggedUsers([]);
  };

  const isPopoverOpen = Boolean(anchorEl);

  if (loading && places.length === 0) {
    return (
      <>
        <div className="loading-container">
          <CircularProgress size={40} sx={{ color: 'black' }} />
        </div>
        <BottomNav />
      </>
    )
  }

  const styles = {
    dialogTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#333",
      textAlign: "center",
      paddingBottom: "8px",
    },
    dialogContent: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    select: {
      backgroundColor: "#f9f9f9",
      borderRadius: "8px",
    },
    dialogActions: {
      justifyContent: "space-between",
      padding: "8px 16px",
    },
    cancelButton: {
      backgroundColor: "#f5f5f5",
      color: "#333",
      textTransform: "none",
      borderRadius: "8px",
    },
    resetButton: {
      backgroundColor: "#ffcccb",
      color: "#333",
      textTransform: "none",
      borderRadius: "8px",
    },
  };

  return (
    <>
      <div style={{ marginTop: "35px" }}>
        <Swiper
          direction="vertical"
          modules={[Mousewheel]}
          mousewheel={true}
          onSwiper={(swiper) => {
            verticalSwiperRef.current = swiper;
            // Start playback for the first visible slide (onSlideChange won't fire on initial mount)
            requestAnimationFrame(() => tryPlayActiveVideoInVertical(swiper));
          }}
          onSlideChange={(swiper) => {
            setActiveVerticalIndex(swiper.activeIndex);

            stopActiveVideo();

            if (swiper.activeIndex === places.length - 1 && !loading) {
              fetchUserFlashes();
            }

            const activeVerticalSlide = swiper.slides[swiper.activeIndex];
            const horizontalSwiper = activeVerticalSlide.querySelector('.swiper-horizontal');
            if (horizontalSwiper) {
              const horizontalActiveIndex = horizontalSwiper.swiper.activeIndex;
              const videoElement = horizontalSwiper.swiper.slides[horizontalActiveIndex].querySelector('video');
              if (videoElement) {
                tryPlayVideo(videoElement);
              }
            }
          }}
          style={{ height: "100vh" }}
        >
          {places.map((place, placeIndex) => (
            <SwiperSlide key={placeIndex}>
              <div>
                <h2 className="flash-title">
                  {place.place_name}
                  <img
                    src={`/assets/flags/${place.country.toLowerCase()}.svg`}
                    alt={place.country}
                    className="country-flag"
                  />
                </h2>

                {locationPermissionGranted && placeIndex === activeVerticalIndex && (
                  <FlashDurations
                    latitude={place.latitude}
                    longitude={place.longitude}
                    userLocation={userLocation}
                    cache={distanceCache}
                    setCache={setDistanceCache}
                  />
                )}
                <Swiper
                  direction="horizontal"
                  modules={[Mousewheel]}
                  mousewheel={true}
                  style={{ height: "80vh" }}
                  onSwiper={(swiper) => {
                    // If this horizontal swiper belongs to the active vertical slide, start playback
                    requestAnimationFrame(() => tryPlayActiveVideoInHorizontal(swiper));
                  }}
                  onSlideChange={(swiper) => {
                    setActiveHorizontalIndex(swiper.activeIndex);

                    stopActiveVideo();

                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const videoElement = activeSlide.querySelector('video');

                    if (videoElement) {
                      tryPlayVideo(videoElement);
                    }
                  }}
                >
                  {place.posts.map((post, postIndex) => {
                    const timeAgo = getTimeAgo(post.created_at);

                    return (
                      <SwiperSlide key={postIndex}>
                        <div>
                          <div className="flash-container">
                            {post.tags && post.tags.length > 0 && (
                              <span
                                className="tag-span"
                                onClick={(e) => handleOpenPopover(e, post.tags)}
                              >
                                +{post.tags.length}
                              </span>
                            )}
                            <img
                              src={post.profile_url || "/assets/images/default_profile.png"}
                              alt={post.username}
                              onClick={(e) => (post.tags && post.tags.length > 0) ? handleOpenPopover(e, post.tags) : navigate(`/profile/${post.user_id}`)}
                              className="flash-profile-img"
                            />
                            <div className="inner-div">
                              <span
                                className="flash-username-span"
                                onClick={() => navigate(`/profile/${post.user_id}`)}
                              >
                                {post.username}
                              </span>
                              <span className="flash-timestamp-span">{timeAgo}</span>
                            </div>

                            <IconButton
                              onClick={() => setIsFilterDialogOpen(true)}
                              style={{ position: "absolute", right: "1.25rem" }}
                            >
                              <FilterListIcon />
                            </IconButton>

                            <Popover
                              open={isPopoverOpen}
                              anchorEl={anchorEl}
                              onClose={handleClosePopover}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                              sx={{
                                "& .MuiPopover-paper": {
                                  backgroundColor: "#f9f9f9",
                                  boxShadow: "0 0 2px rgba(142, 142, 142, 0.1)",
                                  borderRadius: "10px",
                                  padding: "0",
                                  minWidth: "200px",
                                },
                              }}
                            >
                              <List>
                                {taggedUsers.map((tag) => (
                                  <ListItem key={tag.tag_id} onClick={() => navigate(`/profile/${tag.user_id}`)}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#333",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <img
                                        src={tag.profile_url || "/assets/images/default_profile.png"}
                                        alt={tag.username}
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                      {tag.username}
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            </Popover>
                          </div>

                          <div className="flash-content">
                            {post.isVideo ? (
                              <div style={{ position: 'relative' }}>
                                <video
                                  src={post.content_url}
                                  muted={!soundOn}
                                  loop
                                  playsInline
                                  preload="metadata"
                                  style={{ width: '100%', height: 'auto' }}
                                />
                                <IconButton
                                  onClick={toggleSound}
                                  aria-label={soundOn ? 'Mute video' : 'Unmute video'}
                                  sx={{
                                    position: 'absolute',
                                    right: 15,
                                    bottom: 15,
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.45)' },
                                  }}
                                >
                                  {soundOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                                </IconButton>
                              </div>
                            ) : (
                              <img
                                src={post.content_url}
                                alt="Flash"
                                style={{ width: '100%', height: 'auto' }}
                              />
                            )}
                          </div>

                          <div className="flash-icons">
                            <div className="inner-flash-icons">
                              <button
                                onClick={() =>
                                  handleLike(post.flash_id, post.is_liked)
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className={`fa${post.is_liked ? "s" : "r"} fa-heart`}
                                  style={{
                                    fontSize: "28px",
                                    color: post.is_liked ? "#ef4444" : "#6b7280",
                                  }}
                                ></i>
                              </button>

                              <button
                                onClick={() => openDrawer(post.flash_id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="far fa-comment"
                                  style={{
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                    fontSize: "28px",
                                    color: "#6b7280",
                                  }}
                                ></i>
                              </button>

                              <IconButton
                                onClick={() =>
                                  redirectToGoogleMaps(post.latitude, post.longitude)
                                }
                                style={{
                                  padding: "0",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <PublicIcon
                                  style={{
                                    fontSize: "28px",
                                    color: "#6b7280",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <Drawer
          anchor="bottom"
          open={isDrawerOpen}
          onClose={closeDrawer}
          PaperProps={{
            style: { borderTopLeftRadius: 20, borderTopRightRadius: 20 },
          }}
        >
          {selectedPostId && <Comments flashId={selectedPostId} onClose={closeDrawer} />}
        </Drawer>

        <Dialog
          open={isFilterDialogOpen}
          onClose={() => setIsFilterDialogOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                padding: "14px",
                maxWidth: "400px",
                width: "100%",
              },
            },
          }}
        >
          <DialogTitle style={styles.dialogTitle}>Filter Flashes</DialogTitle>
          <DialogContent style={styles.dialogContent}>
            <Select
              fullWidth
              value={selectedCountry}
              onChange={handleCountryChange}
              displayEmpty
              variant="outlined"
              sx={{
                backgroundColor: "#f9f9f9",
                border: "none",
                padding: "4px 0",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="" disabled>
                Select Country
              </MenuItem>
              {countriesISO.map((country) => (
                <MenuItem key={country} value={country}>
                  {getCountryName(country.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
            <Select
              fullWidth
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              displayEmpty
              disabled={!selectedCountry}
              variant="outlined"
              sx={{
                backgroundColor: "#f9f9f9",
                border: "none",
                padding: "4px 0",
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              <MenuItem value="" disabled>
                Select City
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions style={styles.dialogActions}>
            <Button onClick={() => setIsFilterDialogOpen(false)} style={styles.cancelButton}>Cancel</Button>
            <Button onClick={resetFilters} style={styles.resetButton}>
              Reset Filters
            </Button>
            <Button
              onClick={applyFilter}
              disabled={!selectedCity}
              sx={{
                backgroundColor: !selectedCity ? "#ccc" : "#2c3e50",
                color: !selectedCity ? "#fff" : "#fff",
                textTransform: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                "&:hover": {
                  backgroundColor: !selectedCity ? "#ccc" : "#2c3e50",
                },
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <BottomNav />
    </>
  );
};

export default UserFlashes;